/**
 * ================================================================================
 * REUSABLE MODAL COMPONENTS
 * ================================================================================
 * 
 * This file contains generic, reusable modal components that can be used
 * throughout the application. These components are designed to be:
 * - Lightweight and performant
 * - Accessible and keyboard-friendly
 * - Styled using CSS classes from App.css
 * - Easy to customize through props
 * 
 * Components included:
 * - ConfirmationModal: For destructive actions requiring user confirmation
 * - SuccessNotification: For showing success messages as toast notifications
 * ================================================================================
 */

import React from 'react';

// ================================================================================
// CONFIRMATION MODAL COMPONENT
// ================================================================================

/**
 * ConfirmationModal Component
 * 
 * A sophisticated confirmation dialog for destructive or important actions.
 * Provides a clear, accessible way to confirm user actions before proceeding.
 * 
 * @param {boolean} isOpen - Controls modal visibility
 * @param {function} onClose - Callback when user cancels or closes modal
 * @param {function} onConfirm - Callback when user confirms the action
 * @param {string} title - Modal title (e.g., "Delete Item")
 * @param {string|React.Node} message - Message content (can be string or JSX)
 * @param {boolean} isLoading - Shows loading state on confirm button
 * @param {string} confirmText - Text for confirm button (default: "Confirm")
 * @param {string} cancelText - Text for cancel button (default: "Cancel")
 * @param {boolean} isDestructive - Whether action is destructive (affects styling)
 * 
 * @example
 * <ConfirmationModal
 *   isOpen={showModal}
 *   onClose={() => setShowModal(false)}
 *   onConfirm={handleDelete}
 *   title="Delete Item"
 *   message="Are you sure you want to delete this item?"
 *   isLoading={isDeleting}
 *   confirmText="Delete"
 *   isDestructive={true}
 * />
 */
export const ConfirmationModal = ({ 
  isOpen, 
  onClose, 
  onConfirm, 
  title, 
  message, 
  isLoading = false,
  confirmText = "Confirm",
  cancelText = "Cancel",
  isDestructive = false
}) => {
  // Handle escape key press - MUST be called before any conditional returns
  React.useEffect(() => {
    const handleEscape = (event) => {
      if (event.key === 'Escape' && !isLoading) {
        onClose();
      }
    };

    if (isOpen) {
      document.addEventListener('keydown', handleEscape);
      // Prevent body scroll when modal is open
      document.body.style.overflow = 'hidden';
    }

    return () => {
      document.removeEventListener('keydown', handleEscape);
      document.body.style.overflow = 'unset';
    };
  }, [isOpen, isLoading, onClose]);

  // Don't render if not open - AFTER all hooks
  if (!isOpen) return null;

  // Handle backdrop click
  const handleBackdropClick = (event) => {
    if (event.target === event.currentTarget && !isLoading) {
      onClose();
    }
  };

  return (
    <div className="modal-overlay" onClick={handleBackdropClick}>
      <div className="modal-content" role="dialog" aria-modal="true" aria-labelledby="modal-title">
        <h3 id="modal-title" className="modal-header">
          {isDestructive && '⚠️ '}{title}
        </h3>
        <div className="modal-message">
          {message}
        </div>
        <div className="modal-buttons">
          <button 
            onClick={onClose} 
            disabled={isLoading} 
            className="modal-button-cancel"
            type="button"
          >
            {cancelText}
          </button>
          <button 
            onClick={onConfirm} 
            disabled={isLoading} 
            className="modal-button-confirm"
            type="button"
            autoFocus
          >
            {isLoading ? 'Processing...' : confirmText}
          </button>
        </div>
      </div>
    </div>
  );
};

// ================================================================================
// SUCCESS NOTIFICATION COMPONENT
// ================================================================================

/**
 * SuccessNotification Component
 * 
 * A toast-style notification that appears in the top-right corner to show
 * success messages. Non-intrusive and auto-dismisses after a set time.
 * 
 * @param {boolean} isOpen - Controls notification visibility
 * @param {string} message - Success message to display
 * @param {function} onClose - Callback when user manually closes notification
 * @param {number} autoHideDuration - Time in milliseconds before auto-hide (default: 3000)
 * @param {string} icon - Custom icon (default: ✅)
 * 
 * @example
 * <SuccessNotification
 *   isOpen={showSuccess}
 *   message="Item deleted successfully"
 *   onClose={() => setShowSuccess(false)}
 *   autoHideDuration={5000}
 * />
 */
export const SuccessNotification = ({ 
  isOpen, 
  message, 
  onClose, 
  autoHideDuration = 3000,
  icon = "✅"
}) => {
  // Auto-hide functionality - MUST be called before any conditional returns
  React.useEffect(() => {
    if (isOpen && autoHideDuration > 0) {
      const timer = setTimeout(() => {
        onClose();
      }, autoHideDuration);

      return () => clearTimeout(timer);
    }
  }, [isOpen, autoHideDuration, onClose]);

  // Don't render if not open - AFTER all hooks
  if (!isOpen) return null;

  return (
    <div 
      className="success-notification" 
      role="alert" 
      aria-live="polite"
    >
      <span>{icon} {message}</span>
      <button 
        onClick={onClose} 
        className="success-notification-close"
        type="button"
        aria-label="Close notification"
      >
        ×
      </button>
    </div>
  );
};

// ================================================================================
// ERROR NOTIFICATION COMPONENT (BONUS)
// ================================================================================

/**
 * ErrorNotification Component
 * 
 * Similar to SuccessNotification but for error messages.
 * Uses red styling to indicate errors.
 * 
 * @param {boolean} isOpen - Controls notification visibility
 * @param {string} message - Error message to display
 * @param {function} onClose - Callback when user manually closes notification
 * @param {number} autoHideDuration - Time in milliseconds before auto-hide (default: 5000)
 * @param {string} icon - Custom icon (default: ❌)
 */
export const ErrorNotification = ({ 
  isOpen, 
  message, 
  onClose, 
  autoHideDuration = 5000,
  icon = "❌"
}) => {
  // Auto-hide functionality - MUST be called before any conditional returns
  React.useEffect(() => {
    if (isOpen && autoHideDuration > 0) {
      const timer = setTimeout(() => {
        onClose();
      }, autoHideDuration);

      return () => clearTimeout(timer);
    }
  }, [isOpen, autoHideDuration, onClose]);

  // Don't render if not open - AFTER all hooks
  if (!isOpen) return null;

  return (
    <div 
      className="error-notification" 
      role="alert" 
      aria-live="assertive"
    >
      <span>{icon} {message}</span>
      <button 
        onClick={onClose} 
        className="error-notification-close"
        type="button"
        aria-label="Close notification"
      >
        ×
      </button>
    </div>
  );
};

// ================================================================================
// UTILITY FUNCTIONS
// ================================================================================

/**
 * Hook for managing modal state
 * 
 * @param {boolean} initialState - Initial open/closed state
 * @returns {Array} [isOpen, openModal, closeModal]
 */
export const useModal = (initialState = false) => {
  const [isOpen, setIsOpen] = React.useState(initialState);

  const openModal = React.useCallback(() => setIsOpen(true), []);
  const closeModal = React.useCallback(() => setIsOpen(false), []);

  return [isOpen, openModal, closeModal];
};

/**
 * Hook for managing notification state with auto-hide
 * 
 * @param {number} duration - Auto-hide duration in milliseconds
 * @returns {Array} [isVisible, showNotification, hideNotification]
 */
export const useNotification = (duration = 3000) => {
  const [isVisible, setIsVisible] = React.useState(false);

  const showNotification = React.useCallback(() => {
    setIsVisible(true);
  }, []);

  const hideNotification = React.useCallback(() => {
    setIsVisible(false);
  }, []);

  // Auto-hide functionality
  React.useEffect(() => {
    if (isVisible && duration > 0) {
      const timer = setTimeout(hideNotification, duration);
      return () => clearTimeout(timer);
    }
  }, [isVisible, duration, hideNotification]);

  return [isVisible, showNotification, hideNotification];
}; 